import { fetchAdminUserSenderProfileSettings } from '@/api/admin';
import type { SenderProfileSettings } from '@/api/sender-panel';
import { Spinner } from '@/components/ui/spinner';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { SettingsIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface SenderSettingsSubtabProps {
	profileId: number;
}

export default function SenderSettingsSubtab({ profileId }: SenderSettingsSubtabProps) {
	const [settings, setSettings] = useState<SenderProfileSettings | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchAdminUserSenderProfileSettings(profileId);
			setSettings(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId]);

	useEffect(() => {
		void load(true);
	}, [load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error || !settings) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить настройки sender.'
				onRetry={() => void load(true)}
			/>
		);
	}

	const hasValues =
		settings.landingDefaultBuildId ||
		settings.deviceDefaultBuildId ||
		(settings.autoRunDefaultCaption ?? '').trim();

	return (
		<div className='rounded-xl border border-border bg-card px-3 py-3 text-sm flex flex-col gap-3'>
			<div className='flex items-center gap-2 text-muted-foreground'>
				<SettingsIcon size={16} />
				<span className='font-medium text-foreground'>Настройки профиля</span>
			</div>

			{!hasValues ? (
				<p className='text-muted-foreground'>Настройки не заданы — используются значения по умолчанию.</p>
			) : (
				<dl className='grid gap-2 text-sm'>
					<div>
						<dt className='text-xs text-muted-foreground'>Landing default build</dt>
						<dd className='font-mono break-all'>{settings.landingDefaultBuildId ?? '—'}</dd>
					</div>
					<div>
						<dt className='text-xs text-muted-foreground'>Device default build</dt>
						<dd className='font-mono break-all'>{settings.deviceDefaultBuildId ?? '—'}</dd>
					</div>
					<div>
						<dt className='text-xs text-muted-foreground'>Auto-run caption</dt>
						<dd className='whitespace-pre-wrap break-words'>
							{settings.autoRunDefaultCaption?.trim() ? settings.autoRunDefaultCaption : '—'}
						</dd>
					</div>
				</dl>
			)}
		</div>
	);
}
